class ProductReviews {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.id = sectionEl.dataset.sectionId;
    this.grid = document.getElementById(`reviews-grid-${this.id}`);
    this.pagination = document.getElementById(`reviews-pagination-${this.id}`);
    this.lightbox = document.getElementById(`rev-lightbox-${this.id}`);
    this.cards = this.grid ? Array.from(this.grid.querySelectorAll('.review-card')) : [];
    this.perPage = parseInt(
      this.pagination?.querySelector('[data-per-page]')?.dataset.perPage || 6,
      10
    );
    this.currentFilter = 'all';
    this.filteredCards = [...this.cards];
    this.shownCount = 0;
    this.lightboxItems = [];
    this.lightboxIndex = 0;
    this._keyHandler = null;

    this.init();
  }

  init() {
    // These always run regardless of whether reviews exist
    this.initFormToggle();
    this.initStarPicker();

    // These only run when there are review cards
    if (!this.grid || this.cards.length === 0) return;

    this.hideAll();
    this.showBatch();
    this.initFilters();
    this.initLoadMore();
    this.initMediaThumbs();
    this.initLightbox();
    this.initVideoThumbs();
  }

  hideAll() {
    this.cards.forEach(c => (c.style.display = 'none'));
  }

  showBatch() {
    const batch = this.filteredCards.slice(this.shownCount, this.shownCount + this.perPage);
    batch.forEach(c => (c.style.display = ''));
    this.shownCount += batch.length;
    this.updatePaginationUI();
  }

  updatePaginationUI() {
    if (!this.pagination) return;
    const btn = this.pagination.querySelector('.reviews-load-more');
    const status = this.pagination.querySelector('.reviews-pagination__status');
    const remaining = this.filteredCards.length - this.shownCount;

    if (remaining <= 0) {
      if (btn) btn.style.display = 'none';
      if (status && this.filteredCards.length > 0) {
        status.textContent = `Showing all ${this.filteredCards.length} review${this.filteredCards.length !== 1 ? 's' : ''}`;
      }
    } else {
      if (btn) {
        btn.style.display = '';
        btn.textContent = `Show More Reviews (${remaining} remaining)`;
      }
      if (status) {
        status.textContent = `Showing ${this.shownCount} of ${this.filteredCards.length} review${this.filteredCards.length !== 1 ? 's' : ''}`;
      }
    }
  }

  applyFilter(rating) {
    this.currentFilter = rating;
    this.filteredCards =
      rating === 'all'
        ? [...this.cards]
        : this.cards.filter(c => c.dataset.rating === rating);

    this.hideAll();
    this.shownCount = 0;
    this.showBatch();
  }

  initFilters() {
    const filterBtns = this.section.querySelectorAll('.reviews-filter');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.applyFilter(btn.dataset.filter);
        this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    this.section.querySelectorAll('.rating-bar').forEach(bar => {
      bar.addEventListener('click', () => {
        const matching = this.section.querySelector(`.reviews-filter[data-filter="${bar.dataset.rating}"]`);
        if (matching) matching.click();
      });
    });
  }

  initLoadMore() {
    const btn = this.pagination?.querySelector('.reviews-load-more');
    if (!btn) return;
    btn.addEventListener('click', () => this.showBatch());
  }

  initMediaThumbs() {
    this.grid.addEventListener('click', e => {
      const thumb = e.target.closest('.rev-media-thumb');
      if (!thumb) return;

      const card = thumb.closest('.review-card');
      const thumbs = Array.from(card.querySelectorAll('.rev-media-thumb'));
      const idx = thumbs.indexOf(thumb);

      this.lightboxItems = thumbs.map(t => ({
        type: t.dataset.type,
        src: t.dataset.src,
      }));
      this.lightboxIndex = idx;
      this.openLightbox();
    });
  }

  initLightbox() {
    if (!this.lightbox) return;

    this.lightbox.querySelector('.rev-lightbox__overlay').addEventListener('click', () => this.closeLightbox());
    this.lightbox.querySelector('.rev-lightbox__close').addEventListener('click', () => this.closeLightbox());
    this.lightbox.querySelector('.rev-lightbox__prev').addEventListener('click', () => this.prevMedia());
    this.lightbox.querySelector('.rev-lightbox__next').addEventListener('click', () => this.nextMedia());
  }

  openLightbox() {
    if (!this.lightbox) return;
    this.lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    this.renderLightboxMedia();
    this.updateNavButtons();

    this._keyHandler = e => {
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.prevMedia();
      if (e.key === 'ArrowRight') this.nextMedia();
    };
    document.addEventListener('keydown', this._keyHandler);
    this.lightbox.querySelector('.rev-lightbox__close').focus();
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.hidden = true;
    document.body.style.overflow = '';
    this.lightbox.querySelector('.rev-lightbox__media').innerHTML = '';
    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  prevMedia() {
    this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxItems.length) % this.lightboxItems.length;
    this.renderLightboxMedia();
  }

  nextMedia() {
    this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxItems.length;
    this.renderLightboxMedia();
  }

  renderLightboxMedia() {
    const container = this.lightbox.querySelector('.rev-lightbox__media');
    const item = this.lightboxItems[this.lightboxIndex];
    container.innerHTML = '';

    if (item.type === 'image') {
      const img = document.createElement('img');
      img.src = item.src;
      img.alt = 'Review photo';
      container.appendChild(img);
    } else if (item.type === 'video') {
      const iframe = document.createElement('iframe');
      iframe.src = this.getEmbedUrl(item.src);
      iframe.frameBorder = '0';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      container.appendChild(iframe);
    }
  }

  updateNavButtons() {
    const prev = this.lightbox.querySelector('.rev-lightbox__prev');
    const next = this.lightbox.querySelector('.rev-lightbox__next');
    const show = this.lightboxItems.length > 1;
    prev.style.display = show ? '' : 'none';
    next.style.display = show ? '' : 'none';
  }

  getEmbedUrl(url) {
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;

    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;

    return url;
  }

  initVideoThumbs() {
    this.grid.querySelectorAll('.rev-media-thumb--video').forEach(thumb => {
      const bg = thumb.querySelector('.rev-media-thumb__video-bg');
      if (!bg) return;
      const url = bg.dataset.videoUrl || thumb.dataset.src;
      const ytMatch = url && url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
      if (ytMatch) {
        bg.style.backgroundImage = `url(https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg)`;
      }
    });
  }

  initFormToggle() {
    const btn = this.section.querySelector('.reviews-write-btn');
    if (!btn) return;

    const collapseId = btn.dataset.collapseId;
    const collapse = document.getElementById(collapseId);
    if (!collapse) return;

    // Auto-open if form was just submitted successfully
    const successMarker = collapse.querySelector('.reviews-form-success-marker');
    if (successMarker) {
      this.openForm(btn, collapse);
      collapse.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    btn.addEventListener('click', () => {
      const isOpen = collapse.classList.contains('is-open');
      if (isOpen) {
        this.closeForm(btn, collapse);
      } else {
        this.openForm(btn, collapse);
        collapse.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  openForm(btn, collapse) {
    collapse.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    btn.querySelector('.reviews-write-btn__label').textContent = 'Close Form';
  }

  closeForm(btn, collapse) {
    collapse.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    btn.querySelector('.reviews-write-btn__label').textContent = 'Write a Review';
  }

  initStarPicker() {
    const picker = document.getElementById(`star-picker-${this.id}`);
    if (!picker) return;

    const ratingInput = document.getElementById(`rating-val-${this.id}`);
    const stars = Array.from(picker.querySelectorAll('.star-picker__btn'));
    let selectedValue = 0;

    const highlight = upTo => {
      stars.forEach((s, i) => {
        s.classList.toggle('hover', i <= upTo);
      });
    };

    const select = value => {
      selectedValue = value;
      stars.forEach((s, i) => {
        s.classList.toggle('selected', i < value);
        s.classList.remove('hover');
        s.setAttribute('aria-checked', i < value ? 'true' : 'false');
      });
      if (ratingInput) ratingInput.value = value;
    };

    stars.forEach((star, i) => {
      star.addEventListener('mouseenter', () => highlight(i));
      star.addEventListener('click', () => select(i + 1));
    });

    picker.addEventListener('mouseleave', () => {
      stars.forEach((s, i) => {
        s.classList.toggle('hover', i < selectedValue);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[id^="product-reviews-"]').forEach(el => {
    if (el.dataset.sectionId) new ProductReviews(el);
  });
});

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('[id^="product-reviews-"]');
    if (el && el.dataset.sectionId) new ProductReviews(el);
  });
}
